import { t } from "../i18n/t";

/**
 * §B5.2. The value arrives from the API as a decimal **string** at its stored scale (§A3.5: a `Numeric(20,6)`
 * control total arrives as `"0.010000"`). It is never passed through the numeric constructor, the float parser or
 * a locale formatter — eslint bans all three in this file, and `tests/money.test.ts` greps this source for them,
 * which is why none of the three names is written out here — and every transformation below is string surgery.
 *
 * Digits beyond the currency's minor unit are **kept** and rendered in a dimmer span rather than rounded away:
 * that residual is the whole reason a comparison row exists.
 */

/** Mirrors `canonical.money.minor_unit_scale`. Anything not listed is 2. */
const MINOR_UNIT_SCALE: Readonly<Record<string, number>> = {
  BHD: 3,
  BIF: 0,
  CLP: 0,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KRW: 0,
  KWD: 3,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XOF: 0,
  XPF: 0,
};

export function minorUnitScale(currency: string | null | undefined): number {
  if (currency === null || currency === undefined) return 2;
  const scale = MINOR_UNIT_SCALE[currency.toUpperCase()];
  return scale === undefined ? 2 : scale;
}

/** Group an unsigned digit string in threes. String only: no Number, no Intl. */
export function groupThousands(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export type MoneyParts = {
  /** U+2212 MINUS SIGN, or the empty string. Never a hyphen. */
  sign: string;
  integer: string;
  minor: string;
  residual: string;
};

export function moneyParts(value: string, scale: number): MoneyParts {
  const trimmed = value.trim();
  const negative = trimmed.startsWith("-") || trimmed.startsWith("−");
  const unsigned = negative ? trimmed.slice(1) : trimmed;
  const dot = unsigned.indexOf(".");
  const rawInteger = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fraction = dot === -1 ? "" : unsigned.slice(dot + 1);
  return {
    sign: negative ? "−" : "",
    integer: groupThousands(rawInteger === "" ? "0" : rawInteger),
    minor: fraction.slice(0, scale).padEnd(scale, "0"),
    // Trailing zeros are not information; a non-zero digit past the minor unit is.
    residual: fraction.slice(scale).replace(/0+$/, ""),
  };
}

export function Money({
  value,
  currency,
  className,
}: {
  value: string | null | undefined;
  currency?: string | null;
  className?: string;
}): JSX.Element {
  if (value === null || value === undefined || value === "") {
    return (
      <span className={className === undefined ? "money" : `money ${className}`}>{t("common.none")}</span>
    );
  }
  const scale = minorUnitScale(currency);
  const parts = moneyParts(value, scale);
  return (
    <span className={className === undefined ? "money" : `money ${className}`}>
      {parts.sign}
      {parts.integer}
      {scale > 0 ? `.${parts.minor}` : ""}
      {parts.residual === "" ? null : (
        <span className="money__residual" title={t("money.residual")}>
          {parts.residual}
        </span>
      )}
    </span>
  );
}
